import { useCallback } from "react";
import {
  Backdrop,
  Box,
  Fade,
  IconButton,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { queryClient } from "@/queryClient";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import WhatsappQrContent from "./whatsapp-qr-content";

interface WhatsappQrModalProps {
  open: boolean;
  onClose?: () => void;
  isBlocked?: boolean;
  isSetup?: boolean;
}

const HEADER_TITLE = "Whatsapp business setup";

const WhatsappQrModal = ({
  open,
  onClose,
  isBlocked = false,
  isSetup = false,
}: WhatsappQrModalProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  const handleClose = useCallback(() => {
    queryClient.invalidateQueries(["userPassport"]);
    onClose?.();
  }, [onClose]);

  const canClose = !isBlocked && !!onClose;

  const body = (
    <WhatsappQrContent
      enabled={open}
      businessApp={isSetup}
      fillHeight
      onConnected={handleClose}
    />
  );

  if (isMobile) {
    return (
      <MobileModal
        open={open}
        onClose={isBlocked ? () => {} : handleClose}
        title={HEADER_TITLE}
        snapPoints={[1]}
      >
        {body}
      </MobileModal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={isBlocked ? undefined : handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 400 } }}
      sx={{ zIndex: 1500 }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 480,
            maxWidth: 480,
            maxHeight: "calc(100vh - 32px)",
            minHeight: 520,
            display: "flex",
            flexDirection: "column",
            borderRadius: "16px",
            overflow: "hidden",
            backgroundColor: theme.nawy.colors.semantic.layer.primary,
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            outline: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.nawy.spacing.sm,
              py: theme.nawy.spacing.md,
              px: theme.nawy.spacing.lg,
              borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            }}
          >
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.headline.xs,
                color: theme.nawy.colors.semantic.text.primary,
                flex: 1,
                minWidth: 0,
              }}
            >
              {HEADER_TITLE}
            </Typography>
            {canClose && (
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ color: theme.nawy.colors.semantic.text.primary }}
              >
                <X size={18} />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              p: theme.nawy.spacing.lg,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {body}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default WhatsappQrModal;
